import { createRoute, useNavigate, Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Monitor,
  Radio,
  Camera,
  Watch,
  Plus,
  Package,
  ChevronRight,
  Trash2,
  AlertTriangle,
  Lock,
  X,
  Sparkles,
  Gem,
  Gamepad2,
  Bike,
  Music,
  BookOpen,
  Shirt,
  Palette,
  Trophy,
  Coins,
  Stamp,
  Wrench,
  Wine,
  Dices,
  Flower2,
  type LucideIcon,
} from 'lucide-react';
import { getAllVaultTypes, isCustomVaultType } from '@siliconvault/shared';
import type { VaultTypeId } from '@siliconvault/shared';
import { useVault } from '../context/VaultContext';
import type { Vault } from '../context/VaultContext';
import { useAppMode } from '../context/AppModeContext';
import { useTranslation } from '../i18n';
import type { TranslationKey } from '../i18n';
import { Shell } from '../components/layout/Shell';
import { CategoryIcon } from '../components/ui/CategoryIcon';
import { Route as rootRoute } from './__root';
import styles from './vaults.module.css';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vaults',
  component: VaultsPage,
});

const VAULT_ICONS: Record<string, LucideIcon> = {
  Monitor,
  Radio,
  Camera,
  Watch,
  Package,
  Gem,
  Gamepad2,
  Bike,
  Music,
  BookOpen,
  Shirt,
  Palette,
  Trophy,
  Coins,
  Stamp,
  Wrench,
  Wine,
  Dices,
  Flower2,
};

function VaultsPage() {
  const { vaults, createVault, deleteVault, setActiveVaultId, isLoading } = useVault();
  const { mode, aiTier } = useAppMode();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [creating, setCreating] = useState<VaultTypeId | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Vault | null>(null);
  const [deleteInput, setDeleteInput] = useState('');
  const [deleting, setDeleting] = useState(false);
  const allTypes = getAllVaultTypes();

  const deleteConfirmText = t('vaults.deleteConfirmText');

  const userVaultTypes = new Set(vaults.map((v) => v.vaultType));
  // Free web users are limited to 1 vault; desktop users and Pro users get unlimited
  const isVaultLimited = mode === 'web' && aiTier === 'free' && vaults.length >= 1;

  function vaultLabel(id: string): string {
    const key = `vaults.type.${id}` as TranslationKey;
    return t(key);
  }

  function vaultTagline(id: string): string {
    const key = `vaults.tagline.${id}` as TranslationKey;
    return t(key);
  }

  async function handleCreateVault(typeId: VaultTypeId) {
    setCreating(typeId);
    try {
      await createVault(typeId, vaultLabel(typeId));
    } catch {
      return;
    } finally {
      setCreating(null);
    }
    navigate({ to: '/dashboard' });
  }

  function handleEnterVault(vaultId: string) {
    setActiveVaultId(vaultId);
    navigate({ to: '/dashboard' });
  }

  async function handleDeleteVault() {
    if (!deleteTarget || deleteInput !== deleteConfirmText) return;
    setDeleting(true);
    try {
      await deleteVault(deleteTarget.id);
      setDeleteTarget(null);
      setDeleteInput('');
    } catch {
      // error handled by api client
    } finally {
      setDeleting(false);
    }
  }

  if (isLoading) {
    return (
      <Shell>
        <div className={styles.page}>
          <div className={styles.header}>
            <h1 className={styles.title}>{t('vaults.title')}</h1>
            <p className={styles.subtitle}>{t('vaults.subtitle')}</p>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className={styles.page}>
        <div className={styles.header}>
          <h1 className={styles.title}>{t('vaults.title')}</h1>
          <p className={styles.subtitle}>
            {t('vaults.subtitle')}
          </p>
        </div>

        <div className={styles.grid}>
          {/* Existing vaults */}
          {vaults.map((vault) => {
            const isCustom = isCustomVaultType(vault.vaultType);
            const builtInDef = allTypes.find((t) => t.id === vault.vaultType);
            const iconName = isCustom
              ? (vault.customDefinition?.icon ?? 'Package')
              : (builtInDef?.icon ?? 'Package');
            const Icon = VAULT_ICONS[iconName] ?? Package;
            const label = isCustom
              ? (vault.customDefinition?.displayName ?? vault.name)
              : vaultLabel(vault.vaultType);
            const tagline = isCustom
              ? t('vaults.customCard.taglineShort' as TranslationKey)
              : vaultTagline(vault.vaultType);

            return (
              <div key={vault.id} className={styles.vaultCard}>
                <button
                  className={styles.vaultCardMain}
                  onClick={() => handleEnterVault(vault.id)}
                >
                  <div className={styles.vaultCardIcon}>
                    <CategoryIcon icon={Icon} size="xl" />
                  </div>
                  <div className={styles.vaultCardInfo}>
                    <h3 className={styles.vaultCardName}>{label}</h3>
                    <p className={styles.vaultCardType}>{tagline}</p>
                  </div>
                </button>
                <button
                  className={styles.vaultDeleteBtn}
                  onClick={(e) => { e.stopPropagation(); setDeleteTarget(vault); setDeleteInput(''); }}
                  title={t('vaults.deleteBtn')}
                >
                  <Trash2 size={14} strokeWidth={1.75} />
                </button>
              </div>
            );
          })}

          {/* Available vault types (not yet created) */}
          {allTypes
            .filter((vt) => !userVaultTypes.has(vt.id))
            .map((typeDef) => {
              const Icon = VAULT_ICONS[typeDef.icon] ?? Package;
              return (
                <div
                  key={typeDef.id}
                  className={`${styles.vaultCard} ${styles.vaultCardNew}${isVaultLimited ? ` ${styles.vaultCardLocked}` : ''}`}
                >
                  <div className={styles.vaultCardIcon}>
                    <CategoryIcon icon={Icon} size="xl" />
                  </div>
                  <div className={styles.vaultCardInfo}>
                    <h3 className={styles.vaultCardName}>{vaultLabel(typeDef.id)}</h3>
                    <p className={styles.vaultCardType}>{vaultTagline(typeDef.id)}</p>
                  </div>
                  {isVaultLimited ? (
                    <Link to="/upgrade" className={styles.vaultCardBadge} style={{ textDecoration: 'none' }}>
                      <Lock size={14} />
                      {t('vaults.upgradeBadge')}
                    </Link>
                  ) : (
                    <button
                      className={styles.vaultCardBadge}
                      onClick={() => handleCreateVault(typeDef.id)}
                      disabled={creating === typeDef.id}
                    >
                      <Plus size={16} />
                      {t('vaults.create')}
                    </button>
                  )}
                </div>
              );
            })}

          {/* Create Custom Vault card */}
          <div
            className={`${styles.vaultCard} ${styles.vaultCardNew}${isVaultLimited ? ` ${styles.vaultCardLocked}` : ''}`}
          >
            <div className={styles.vaultCardIcon}>
              <CategoryIcon icon={Sparkles} size="xl" />
            </div>
            <div className={styles.vaultCardInfo}>
              <h3 className={styles.vaultCardName}>{t('vaults.customCard.title' as TranslationKey)}</h3>
              <p className={styles.vaultCardType}>{t('vaults.customCard.tagline' as TranslationKey)}</p>
            </div>
            {isVaultLimited ? (
              <Link to="/upgrade" className={styles.vaultCardBadge} style={{ textDecoration: 'none' }}>
                <Lock size={14} />
                {t('vaults.upgradeBadge')}
              </Link>
            ) : (
              <Link to="/vaults/custom" className={styles.vaultCardBadge} style={{ textDecoration: 'none' }}>
                <Plus size={16} />
                {t('vaults.create')}
              </Link>
            )}
          </div>
        </div>

        {isVaultLimited && (
          <div className={styles.upgradeBanner}>
            <Lock size={16} strokeWidth={1.75} />
            <span>{t('vaults.upgradeHint')}</span>
            <Link to="/upgrade" className={styles.upgradeBannerLink}>
              {t('vaults.upgradeCta')}
            </Link>
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className={styles.modalOverlay} onClick={() => setDeleteTarget(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <button
              className={styles.modalClose}
              onClick={() => setDeleteTarget(null)}
            >
              <X size={18} strokeWidth={2} />
            </button>

            <div className={styles.modalWarningIcon}>
              <AlertTriangle size={32} strokeWidth={1.5} />
            </div>

            <h2 className={styles.modalTitle}>
              {t('vaults.deleteTitle').replace('{name}', deleteTarget.name)}
            </h2>

            <p className={styles.modalDesc}>
              {t('vaults.deleteWarning')}
            </p>

            <label className={styles.modalLabel}>
              {t('vaults.deleteConfirmLabel').replace('{confirmation}', deleteConfirmText)}
            </label>
            <input
              className={styles.modalInput}
              type="text"
              value={deleteInput}
              onChange={(e) => setDeleteInput(e.target.value)}
              placeholder={deleteConfirmText}
              autoFocus
            />

            <div className={styles.modalActions}>
              <button
                className={styles.modalCancelBtn}
                onClick={() => setDeleteTarget(null)}
              >
                {t('vaults.cancel')}
              </button>
              <button
                className={styles.modalDeleteBtn}
                onClick={handleDeleteVault}
                disabled={deleteInput !== deleteConfirmText || deleting}
              >
                {deleting ? t('vaults.deleting') : t('vaults.deleteBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}
